// Brief 85 §4 — AgendaEventModal, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN — AT EXTRACTION, WHICH IS THE ONLY THING THE HASH BELOW
// CAN SAY. The statement block was byte-identical to the body of the ag clause
// as it stood in App.tsx —
//   sha256 58fc52fa901f2c0efffe5a3f20343a57e13f22da37415be58c7b4ba6f71574da
// — indentation included, which is why it still sits at the depth it had inside
// App(). Only the imports, the Props type and the signature were new.
//
// IT IS NO LONGER THAT BLOCK, and this line is here because the paragraph above
// went on claiming it was. 87 §4 gave it the Documents pane; 86 Phase 2 step 9
// replaced three regions with shared components; brief 91 §1b moved the
// OneDrive button onto the pane's add row. The hash records the extraction. It
// is not a claim about this file. (RULINGS 2026-08-18: a file asserting a state
// that is not true is the recurring defect, and no gate catches it.)
//
// Do not reformat: the indentation is still the extraction's own.
//
// Props are the UNION of the App-scope names the body actually read, from a
// TypeScript-checker walk rather than chosen. The dialog declares no hook (§0
// measured zero across all 21 blocks), so it moves as a component without any
// state ownership changing hands.

import MultiSelect from "../../components/MultiSelect";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import StageLinkField from "../StageLinkField";
import TaskDocsPane from "../TaskDocsPane";
import type { TaxEntry, Jur } from "../StageLinkField";
import SubtaskChecklist from "../../components/SubtaskChecklist";
import TemplateSubtasksButton from "./TemplateSubtasksButton"; // 86 Phase 2 step 9
import ReminderLevelFields from "./ReminderLevelFields";
import TimeField from "../TimeField"; // 117 §5
import { FS, RAD } from "../../lib/tokens";
import { ModalLayout } from "../../components/SharedUI";
import MatterPicker, { formMatterId, formMatterKind } from "../MatterPicker"; // 131 §2; 184 A.1 — the two helpers
import LinkChainFooter from "../LinkChainFooter"; // 111 §5b
import { coIni, pname } from "../../utils";
// 122 §2 — ONE list. `EventPeopleOverview` (the standalone status block),
// `MultiSelect` ("Should attend") and `AttendeesPickerSection` (the invitee
// checkboxes) were three surfaces asking one question; this is the question,
// asked once. The two components that survive keep their own call sites — the
// picker is still the doodle dialog's (App.tsx:16396).
import EventAttendeesSection from "../EventAttendeesSection";
import { assignedFromRows, inviteDraftRecipients } from "../../lib/meetingAttendees";
import type { AttendeeRow } from "../../lib/meetingAttendees";
import { RoomSelectField } from "../../components/RoomSelectField";
import { _buildTplItems, _reconcileTpl } from "../../lib/templatePreview";
import { isDividerCode } from "../../utils/taxonomyMap"; // 110 §3a
import {
  pickableEventStatusCodes,
  EVENT_STATUS_DEFAULT,
} from "../../lib/eventStatus"; // 136 §3
import { getEventStatusMeta } from "../../lib/taxonomyHelpers"; // 136 §3
import type { TFn, TaxList } from "../../lib/taxonomyTypes";
// 123 §3 — the mode vocabulary, shared with App and with lib/meetingMode's own
// read-side default.
import {
  MEETING_MODES,
  eventSaveWords,
  type MeetingMode,
} from "../../lib/meetingMode";
import FieldSelect from "../FieldSelect"; // 136 §3
import MenuSelect from "../MenuSelect"; // 154B §2
import type { FieldSelectOption } from "../FieldSelect";
import type { makeModalFields } from "../modalFields";

type Vocab = ReturnType<typeof makeModalFields>;

type Props = {
  // Brief 86 §2e-1 — the two App-level permission props the Task modal
  // already takes, so the subtask assignee picker asks the same question here
  // that it asks there (with the agenda key rather than the tasks key).
  _appIsAdmin: boolean;
  _appPerms: {
    can: (sectionKey: string, opts?: { caseId?: unknown; ccId?: unknown }) => boolean;
  };
  S: Vocab["S"];
  T: Vocab["T"];
  _fl: Vocab["_fl"];
  _mt: Vocab["_mt"];
  agCaseQ: string;
  /* ── BRIEF 123 §3 — TWO FACTS, WHERE `EV_MEETING_KIND` + `agMeetKind` WERE ──
     `agIsMeeting` is the TYPE's question, answered by the firm's own taxonomy
     (`meta.isMeeting`, §1) instead of by two Belgian codes hardcoded in the
     browser. `agMode` is the EVENT's, chosen on the toggle this § adds and
     stored on the row (§2); it is null exactly when the type is not a meeting
     type, so `agMode === "person"` / `=== "online"` are false for an ordinary
     event just as `agMeetKind === "room"` / `=== "virtual"` were. */
  agIsMeeting: boolean;
  agMode: MeetingMode | null;
  /* BRIEF 124 §1 — the mode a TYPE declares, resolved by the host from the one
     fold (`meetingDefaultModes`), never re-derived here. A function of the type
     CODE and not of the current form, because the type-change handler asks it
     about the code the user has just picked — the form still holds the old one
     at that moment. `null` = the type declares no preference. */
  agDefaultMode: (typeCode: unknown) => MeetingMode | null;
  agendaEvents: any[];
  canBumpRooms: boolean;
  cases: any[];
  // BRIEF 87 §8 — the stage picker resolves the jurisdiction NAME for §8a's
  // location autofill. App holds the list (App.tsx:1219); nothing passed it here
  // before this brief.
  jurisdictions?: readonly Jur[];
  cm: () => void;
  collaborators: any[];
  collectiveCases: any[];
  currentUser: any;
  evtEntries: TaxList;
  // 109 §7e — the case_stage entries, for stageAutofill's NAME resolve.
  caseStageEntries: TaxList;
  evtTypes: any[];
  form: any;
  genAgLocation: (typeCode: unknown, caseId: unknown) => string;
  getRecentCases: (u: unknown) => readonly unknown[];
  hmPlus60: (hm: unknown) => string;
  itemLevelEntries: TaxList;
  mtgPlatforms: any;
  mtgRoomConflicts: any;
  mtgRooms: any[];
  reminderOptsList: readonly FieldSelectOption[];
  /* 136 §3(5) — the firm's `event_status` rows. Entries, not a tuple list:
     `FieldSelect` resolves each option's language label from them. */
  eventStatusEntries?: TaxList;
  sAgCaseQ: (v: unknown) => void;
  saveAgendaEvent: (d: unknown) => boolean;
  /* 137 §4(d) — `onSaveInvite` STOOD HERE (119 §1c: save this event, then mint
     its meeting through `POST /agenda/meeting`). The second primary is
     withdrawn and the road with it; what replaces it is below, and it is not a
     button. */
  /* 137 §4(c) — the ONE thing that mails anybody. Called with the SAVED row's
     id, the two recipient lines this modal derives from its own ticked roster,
     and a window the click already opened. App owns it because the POST and
     its toasts are App's; the roster and therefore the split are this file's. */
  onInviteDraft: (
    eventId: number,
    rcpt: { to: string[]; cc: string[] },
    win: Window | null,
  ) => void;
  /* 111 §5b — the link chain: chainEvent(null) is the plain link-aware save
     (saveAgendaEvent + links + cm); chainEvent(kind) spawns the next modal.
     The meeting guards stay HERE, beside the state they read, and run before
     either. `onSaved` is 137 §4(c)'s hook: the saved row's id, on both the
     create and the edit branch. */
  chainEvent: (
    next: "stage" | "task" | "event" | null,
    onSaved?: (id: number) => void,
  ) => boolean;
  /* 122 §3b's `askConfirm` IS GONE — this modal's only use of it was the
     send-update confirmation, and 137 §4 removed the send it asked about. The
     dialog itself is App's and is still there for its other callers. */
  linkState: {
    unlinkDisabled: boolean;
    spawnDisabled: boolean;
    onUnlinkAll: () => void;
    /* 112 §7 — the Links manager's door. */
    linkDisabled: boolean;
    onLinkManager: () => void;
  };
  setAgendaEvents: (v: unknown) => void;
  setForm: (v: unknown) => void;
  t: TFn;
  toast: (m?: string, type?: string, opts?: { action?: unknown }) => void;
  updateField: (k: string, v: unknown) => void;
};

export default function AgendaEventModal({
  _appIsAdmin,
  _appPerms,
  S,
  T,
  _fl,
  _mt,
  agCaseQ,
  agIsMeeting,
  agMode,
  agDefaultMode,
  agendaEvents,
  canBumpRooms,
  cases,
  cm,
  collaborators,
  collectiveCases,
  currentUser,
  jurisdictions,
  evtEntries,
  caseStageEntries,
  evtTypes,
  form,
  genAgLocation,
  getRecentCases,
  hmPlus60,
  itemLevelEntries,
  mtgPlatforms,
  mtgRoomConflicts,
  mtgRooms,
  reminderOptsList,
  eventStatusEntries,
  sAgCaseQ,
  saveAgendaEvent,
  onInviteDraft,
  chainEvent,
  linkState,
  setAgendaEvents,
  setForm,
  t,
  toast,
  updateField,
}: Props) {
  /* Phase 29 — save logic extracted to svAgData (shared with the
     compact column quick-add). Corr-2 (item 8) — meeting-type
     pre-checks mirror the Parties-pane modal: pick-time room
     conflicts block (unless overridden), Teams needs date+time
     to mint the join URL, pasted links get light validation.
     The server re-validates all three (409/400 backstops).
     111 §5b — the guards move into `_guards` so the chain footer's five
     buttons run the SAME checks the plain save always ran. */
  const _guards = () => {
    /* 123 §3 — the mode, not the type code. */
    if (
      agMode === "person" &&
      form.roomPlaceId &&
      mtgRoomConflicts &&
      !form.roomOverride
    ) {
      toast(
        t("modal.meeting.room_conflict_block", {
          fallback:
            "Room conflict — pick another room or time, or bump the existing reservation.",
        }),
        "error",
      );
      return false;
    }
    if (agMode === "online") {
      const plat =
        form.platform ||
        (mtgPlatforms.teams
          ? "teams"
          : mtgPlatforms.zoom
            ? "zoom"
            : "gmeet");
      if (plat === "teams" && !form.id && (!form.date || !form.time)) {
        toast(
          t("modal.ag.time_required", {
            fallback:
              "An online meeting needs a date and a time",
          }),
          "error",
        );
        return false;
      }
      const link =
        plat !== "teams" ? String(form.meetingUrl || "").trim() : "";
      if (link && !/^https?:\/\/\S+$/i.test(link)) {
        toast(
          t("modal.meeting.bad_link", {
            fallback:
              "The meeting link must be a full http(s):// address",
          }),
          "error",
        );
        return false;
      }
    }
    return true;
  };
  /* BRIEF 117 §6 — WILL THIS SAVE MINT A MEETING AND INVITE PEOPLE?
     The server's own condition, read off `routes/agenda.js:385`: the type maps
     to the VIRTUAL meeting kind AND the platform is `teams`. The platform
     fallback is `_guards`'s, unchanged — the modal has always resolved an unset
     `form.platform` to the firm's first enabled option. Editing an existing row
     does NOT re-mint (the route's Teams leg is the CREATE path; `form.id` locks
     the platform select for the same reason), so both §6a's label and §6b's
     picker are creation-only. */
  /* ── 119 §5 — THE EVENT'S PEOPLE, GROUPED ONCE ────────────────────────────
     The matter's team is what separates (a) from (b), and it is resolved HERE
     because this is where the matter is known. `form.caId` carries a Case id or
     a CollectiveCase id depending on the picker (the ag modal's own long-
     standing shape), so both ledgers are asked — and only a row that matches
     supplies a team, so a matter-less internal event puts every collaborator in
     (b), which is correct rather than a special case. */
  /* 184 A.1 — the form's matter ids, read ONCE and typed: `""` (a cleared
     picker) and null both mean none. A collective event's id is `ccId` (every
     caller seeds it so, and the picker now writes it so); the `caId` lookup in
     both lists is the long-standing shape, kept for a form that still carries
     it. */
  const _caIdF = formMatterId(form.caId);
  const _ccIdF = formMatterId(form.ccId);
  const _matter =
    _ccIdF != null
      ? (collectiveCases.find((c: { id?: number }) => c.id === Number(_ccIdF)) ?? null)
      : (cases.find((c: { id?: number }) => c.id === Number(_caIdF)) ??
        collectiveCases.find((c: { id?: number }) => c.id === Number(_caIdF)) ??
        null);
  /* 185 E.1 — THE MATTER'S PERMISSION SCOPE, derived ONCE: `{ ccId }` for a
     collective event, `{ caseId }` for a case event, `{}` for none — the same
     disambiguation by LIST MEMBERSHIP `_matter` above and the documents pane's
     `oneDrive` binding below perform, because `form.caId` carries either
     kind's id. The assignee gate asked `caseId: form.caseId`, an alias this
     form never carries (its live one is `caId`, 184 A.1), so on a CASE the
     per-matter grant was never consulted — a collaborator holding
     `caseView.agenda.edit` by grant on that one case was shown no picker, and
     a DENY grant was ignored for the role default (usePermissions.ts:87–93
     reads `caseId` only when it arrives). */
  const _matterScope: { caseId?: number; ccId?: number } = (() => {
    if (_ccIdF != null) return { ccId: Number(_ccIdF) };
    if (_caIdF == null) return {};
    const _mid = Number(_caIdF);
    if (cases.some((c: { id?: number }) => c.id === _mid)) return { caseId: _mid };
    if (collectiveCases.some((c: { id?: number }) => c.id === _mid)) return { ccId: _mid };
    return {};
  })();
  /* 122 §2 — `_matter`, `_teamIds` and `_peopleGroups` stood here to build the
     read-only overview. The whole population — the matter's cast, the event's
     own people AND the firm's choosable roster — is now merged ONCE, in
     `meetingRosterFor` (App.tsx), and arrives as `form._attendees`. Resolving
     it a second time in the modal is how two surfaces come to disagree, which
     is the defect §2 exists to remove. */
  /* `_platform` STOOD HERE and is deleted: §2 removed its last reader. It
     resolved an unset `form.platform` to the firm's first enabled option purely
     so 117 §6's condition could ask whether it was `teams`; nothing else read
     it, and the fallback it encoded still lives where it is actually used —
     `saveAgendaEvent` (App.tsx) and the mint. A const kept for a condition that
     no longer exists is a claim about the code that is no longer true. */
  /* ── BRIEF 119 §2 [RULED] — THE INVITE IS OFFERED ON EVERY EVENT KIND ─────
     Laurent: "eg for a hearing, one can want to invite a third party not in the
     app."

     [MEASURED] the invite machinery never cared about the kind.
     `buildUserEvent({ …, attendees, isOnline, locationName })`
     (server/src/lib/m365.js:1442) takes attendee addresses whether or not the
     event is online — `isOnline` decides only whether Graph mints a Teams
     meeting, and `locationName` carries the room or address when it does not.
     The ONLY thing gated on virtual + teams was the CALLER's branch. So the
     gate goes: a hearing, a meeting in person, anything.

     WHAT THE BUTTON READS PER KIND — the enumeration §2 asks for: the SAME two
     words on every kind, "Save & invite". It does not differ, because the ACTION
     does not differ: save this event, then mint its meeting.

     120 §3 — AND THE LEG IS NO LONGER A SECOND SURFACE'S TO CHOOSE. This said
     the dialog picked it and announced it with its own In Person / Virtual
     toggle. There is no dialog: the leg comes from the TYPE this modal already
     carries (`EV_MEETING_KIND`, App.tsx:504@50fb417c — REU room, VIR virtual), which is
     §3's ruling that the type field IS the toggle. A VIRTUAL kind mints a Teams
     meeting; every other kind — a hearing, a meeting in person, anything — does
     not, and the Graph event carries the location while the invitations still go
     out. §2's point survives its mechanism: one vocabulary, one place.

     123 §3 — AND THAT PARAGRAPH IS WITHDRAWN IN PLACE, quoted rather than
     deleted, because it is why the pair was removed and it has to stay legible
     to whoever reads the pair coming back. 120 §3 was right that there must not
     be "a second switch that means the same thing"; it was WRONG that the type
     was that switch. The type answers what kind of event this is — a hearing, a
     submission, a meeting — and the mode answers how it is held. Two questions,
     and collapsing them buried the second one inside a fifteen-entry dropdown
     and left a hearing-by-videoconference with nowhere to be recorded. The In
     Person / Virtual pair is back, at the top of the participants section, as
     PHYSICAL / ONLINE, writing `form.meetingMode`. Both modes send invitations
     (RULED, Laurent 2026-08-23); online additionally mints the join link.

     AND "SAVE & EXIT" STAYS BESIDE IT, unchanged in wording and behaviour — §2's
     own instruction that "the two live together, and the brief does not rule one
     out of existence". An event nobody needs inviting to is still one click.

     122 §3c — AND THE CREATION-ONLY RULE IS HALF LIFTED. What stood here said:
     "offering it on an EXISTING event is 119 §4, which also has to teach the
     route to widen a LIVE Graph event's attendee list rather than mint a second
     meeting. Until that lands an edit keeps the single primary it has always
     had — the invariant §1a enforces (409 `meeting.already_minted`) is what
     would otherwise be met with a refusal the user did not ask for."

     §3c built exactly that route (`POST /agenda/:id/attendees`), so the refusal
     is no longer what an edit would meet, and the primary is offered on an
     existing event WHOSE MEETING IS ALREADY MINTED. It is a different road: a
     new event MINTS (`POST /agenda/meeting`), an already-minted one UPDATES ITS
     ATTENDEES and nothing else.

     HALF, AND THE OTHER HALF STAYS PARKED — 122 §3c is explicit that it
     supersedes 119 §4's parked status ONLY for the attendee half. Whether a
     hearing that never had a meeting can GROW one on edit is still open, which
     is why `_mintedMeeting` and not `form.id` alone: an existing event with no
     `organizerEventId` keeps the single primary it has always had, and the
     route refuses that case (409 `meeting.not_minted`) rather than minting
     behind a question nobody asked.

     ── 119 §4 IS CLOSED. THE PARK IS LIFTED (Laurent, 2026-08-23) ────────────
     THIS IS WHERE THE PARK WAS RECORDED, so this is where its closure goes.
     Brief 123 §5 carried the WORDING for the middle state and not the ruling;
     the ruling was made the same day and did not reach the brief.

     THE OTHER HALF NEEDED NO SERVER WORK, and that is the measurement rather
     than an assumption. [MEASURED @50fb417c] `POST /agenda/meeting`'s `eventId`
     branch (agenda.js:1044-1050) refuses ONLY a row that already carries a
     meeting — `if (row.organizerEventId) return 409 meeting.already_minted`.
     A row whose `organizerEventId` is NULL falls straight through to
     `target = row` and is minted onto. The capability was there the whole
     time; nothing but this flag was stopping it.

     SO THE THREE STATES ARE THREE ROADS, and every one of them exists:
       new event                 POST /agenda/meeting          MINTS
       edit, no meeting yet      POST /agenda/meeting + eventId MINTS onto it
       edit, meeting exists      POST /agenda/:id/attendees     changes WHO
     `saveEventThenInvite`'s `mint` already picks between the last two on
     `_minted`, so no client road is new either.

     `_willInvite` IS GONE rather than widened to `true`: a flag that is always
     true is a claim about a condition that no longer exists. `_mintedMeeting`
     stays — it is what §5's wording, §6's question and §7's line and fence all
     read, and it means something else entirely (is a meeting LIVE). */
  const _mintedMeeting = !!form.id && !!form.organizerEventId;
  /* ── BRIEF 120 §3 — THE CHECKBOXES, AND THE CONDITION THAT DECIDES THEM ─────
     §3 asks for "the roster rendered as CHECKBOXES rather than a read-only
     overview when the event is one people get invited to", and asks the PR to
     enumerate which condition decides it. It is `_willInvite`, and the reason it
     is not a new switch is that 120's Table 3 turned out to be wrong about the
     premise: the overview and the picker are not one list in two presentations,
     they are two POPULATIONS (found.txt D3-1). So there is nothing to choose
     between — rule 2, union not subset — and both render:

       EventPeopleOverview  who is ON this event   (eventPeople, read-only)
       these checkboxes     who gets INVITED       (the matter's cast)

     — BOTH OF WHICH ARE HISTORY as of 122 §2: the two lists became one, and
     `EventPeopleOverview` is deleted. The reasoning is kept because it is why
     120 §3 was right to render two, and 122 §2 unions rather than reverses it.

     which is exactly the sharpening Table 4 point 3 predicted from the other
     side. `_willInvite` is `!form.id` — the same flag that offers "Save &
     invite" — because the mint is creation-only until 119 §4 teaches the route
     to widen a LIVE Graph event's attendee list. An edit therefore shows the
     overview alone, which is what it has always shown.

     `_willInvite` NO LONGER EXISTS — 119 §4 is closed and the invite is offered
     on every state, so the flag it was is gone (see the block above). The two
     sentences quoting it are kept as history, not as coordinates.

     THE STATE IS `_attendees`, UNDERSCORED, and that is measured rather than
     stylistic (found.txt D3-4): `popoverChrome.ts`'s unsaved-work test counts an
     empty array as data, so a bare `attendees: []` would make a pristine New
     Event modal prompt "discard?" on Escape. The underscore is this form's own
     convention for UI-only fields (`_locManual`, `_meetInit`, `_roomLoaded`),
     and `saveAgendaEvent` strips it beside them. */
  const _picker = {
    internal: !!form._internal,
    attendees: (form._attendees || []) as AttendeeRow[],
  };
  /* 122 §2 — ONE TOGGLE, WRITING BOTH THINGS AT ONCE. The tick is the row's;
     `assignedCollaborators` is what the ticked TEAM/FIRM rows produce (§2's own
     words), so it is re-derived from the same array in the same update. Two
     controls that could disagree about who is attending is exactly what §2
     removes, and deriving the field rather than maintaining it beside the list
     is what stops the merge re-creating the disagreement inside one control. */
  const _toggleAttendee = (key: string) =>
    setForm(
      (p: {
        _attendees?: AttendeeRow[];
        assignedCollaborators?: number[];
      }) => {
        const rows = (p._attendees || []).map((r) =>
          r.key === key ? { ...r, checked: !r.checked } : r,
        );
        return {
          ...p,
          _attendees: rows,
          assignedCollaborators: assignedFromRows(rows),
        };
      },
    );
  /* ── BRIEF 122 §3b's SEND-UPDATE CONFIRMATION IS GONE WITH ITS PREMISE ──────
     `_sendDiff` / `_sendMsg` stood here and warned, on the button that sent,
     that Outlook MAY re-notify everybody when an already-live meeting's
     attendee list changes. §3b's whole design was to make the vendor's
     undocumented behaviour not matter by SAYING SO before the send.

     137 §4 removes the send. Graph is never handed an attendee array again
     (§4b) and the route that PATCHed one onto a live meeting is retired (§4e),
     so LexCRM no longer causes Outlook to notify anyone — there is no act left
     for the question to be about, and a warning about an act that cannot
     happen is worse than no warning. The three keys
     (`modal.event.send_update.adding` / `.removing` / `.tail`) fall quiet and
     their seed rows stay (D28).

     `askConfirm` went with it — this modal's only use of the prop.

     WHAT REPLACES IT IS NOT A SENTENCE, IT IS THE SHAPE: the ticks decide who
     is on the draft, the draft is a draft, and the user reads the recipient
     lines in Outlook before anything leaves. */
  /* chainEvent(null) = saveAgendaEvent + pairwise links (when chained) + cm —
     identical to the old `if (saveAgendaEvent(form)) cm()` when un-chained.
     The guards still run FIRST: everything below is about an act the save has
     not yet been allowed to perform.

     123 §6 FOUND A LIVE DEFECT HERE and it is closed by there being one
     button. The warning sat on the arm that ran `chainEvent(null)`, WHICH
     POSTS NO ATTENDEES AT ALL, while `_doSaveInvite` — the only path that
     reached Graph — asked nothing. "The button that warned did not send, and
     the button that sent did not warn." §4(d) removes the second button, and
     §4(b)/(e) remove the send it was about. */
  /* ── BRIEF 137 §4(c) — WHO IS ON THE EVENT DECIDES WHETHER MAIL LEAVES ─────
     [RULED, Laurent 2026-08-25; 138 §2 lifts 137's HARD STOP 1]

     There is one save, and it asks the roster rather than the user. Every
     ticked row with no `coId` is a `third` — a client, an opposing counsel, an
     expert, anyone whose calendar does not sync with this app — and if there is
     even one, the save is followed by ONE Outlook draft addressed to them,
     colleagues in `cc`, the event's .ics attached, for the organiser to
     finish and send himself. If there is none, NOTHING happens: no mail, no
     draft, no dialog. Internals learn of the event in the app, through the
     reminder ladder and on the firm's shared calendar.

     THE WINDOW IS OPENED HERE, SYNCHRONOUSLY, and only when there is going to
     be something to put in it. `window.open` inside the click carries the user
     activation that survives the two awaits between here and the `webLink`
     (the save's persist, then the POST); opening it unconditionally would
     flash a blank tab on every internal save. */
  const _doSave = () => {
    if (!_guards()) return;
    const rcpt = inviteDraftRecipients(_picker.attendees);
    if (!rcpt.to.length) {
      chainEvent(null);
      return;
    }
    // No 'noopener': it makes window.open return null, and the handle is what
    // gets navigated after the await (`emailArchiveClient.ts:93-95`, same
    // reasoning, same trusted destination — the user's own Outlook).
    const win = window.open("about:blank", "_blank");
    const ok = chainEvent(null, (id) => onInviteDraft(id, rcpt, win));
    /* A save the guards passed but `saveAgendaEvent` still refused (no title,
       no date, a create already in flight) leaves the modal open and must not
       leave a blank tab behind it. */
    if (!ok) {
      try {
        win?.close();
      } catch {
        /* a window the browser already closed */
      }
    }
  };
  /* BRIEF 137 §4(d) — THE TWO PRIMARIES BECOME ONE, AND 119 §2 IS WITHDRAWN.
     `_doSaveInvite` stood here: the second primary, which saved with
     `deferMeeting` and then posted `/agenda/meeting` to mint a Graph meeting
     that mailed its attendees. There is no such button and no such road. The
     ONE save above decides from the ticked roster whether a draft follows, so
     the choice a user was being asked to make with a button is now read off
     the thing the choice was always about.

     `_app` WENT WITH IT. It resolved the event's platform only so that
     `platformShortKey` could name it inside an invite label ("Save & send
     Teams invitations"); the plain save's wording names no platform, and
     `eventSaveWords` no longer takes one (`lib/meetingMode.ts`). The event's
     platform itself is untouched — it is still stored, still chosen in the
     select below, still locked after creation. */
  /* ── BRIEF 124 §2 — 123 §7's FENCE IS GONE FROM THIS SCREEN ───────────────
     `_modeLocked = _mintedMeeting && agMode === "online"` stood here and
     disabled the Physical button with `modal.ag.mode_locked_online`. RULED
     (Laurent, 2026-08-23): take the lock off — it will be tested in real use.

     THE SAFETY DID NOT MOVE TO THE CLIENT, and that is why nothing replaces
     this. It moved to the BOUNDARY, where it can actually be enforced:
     `PUT /agenda/:id` performs the removal against Graph BEFORE it writes the
     row, PROVES the meeting is gone rather than trusting a 200, and answers
     502 `meeting.online_removal_refused` with the event untouched when it
     cannot. A disabled button was only ever a way of stopping somebody
     REACHING a boundary that could not answer; the boundary answers now.

     The tooltip key `modal.ag.mode_locked_online` stays seeded (D28) — it is
     tenant data and removing a translation row is not this brief's to do. */
  /* BRIEF 124 §4 — the room this event is actually in, by the same precedence
     the row uses: the server's derived `roomName` (from the bound
     PlaceReservation) first, then the picker's live choice, so the note below
     the location field appears while the room is being chosen and not only
     after it has been saved. */
  const _boundRoom: string | null =
    (typeof form.roomName === "string" && form.roomName) ||
    (form.roomPlaceId != null
      ? ((mtgRooms || []).find(
          (r: { id?: number | string; name?: string }) =>
            String(r.id) === String(form.roomPlaceId),
        )?.name ?? null)
      : null) ||
    null;
  const _words = eventSaveWords({ isNew: !form.id });
  const _doSpawn = (k: "stage" | "task" | "event") => {
    if (!_guards()) return;
    chainEvent(k);
  };
  return (
          <ModalLayout
            title={form.id ? _mt("Edit Event") : _mt("New Event")}
            onClose={cm}
            onSave={_doSave}
            footer={
              <LinkChainFooter
                kind="event"
                onSave={_doSave}
                /* 137 §4(d) — ONE SAVE. `onSaveInvite`/`inviteLabel` are gone
                   from this call AND from the footer's props: no other host
                   ever passed them. 117 §6a's `saveLabel` STAYS — a label is
                   the one thing that may distinguish the three saves 138 §1
                   made identical, and this is still the only host that knows
                   whether its event is new. */
                saveLabel={t(_words.saveKey)}
                onSpawn={_doSpawn}
                spawnDisabled={linkState.spawnDisabled}
                unlinkDisabled={linkState.unlinkDisabled}
                onUnlinkAll={linkState.onUnlinkAll}
                linkDisabled={linkState.linkDisabled}
                onLinkManager={linkState.onLinkManager}
                onCancel={cm}
              />
            }
          >
              <>
                {/* 184 A.1 [RULED] — THE FIELD SHOWS INSIDE A CASE TOO, prefilled
                    with the current matter; changing it is a MOVE the server
                    guards (agenda.js PUT: the target right, the stage link, the
                    item links, the time entries). The `!form._caseFixed &&`
                    guard is gone; the key's writers stay and are inert here.
                    MEASURED, and the brief's "its picker writes caId/ccId" was
                    not so: the picker read `form.caId` alone and wrote `caId`
                    for BOTH kinds, so a collective event opened from its own
                    pane (seeded `ccId`, 117 §8) would have shown an EMPTY
                    field, and a collective pick would have stored the cc's id
                    as `caseId`. It now reads and writes both, in TaskModal's
                    shape (`m._type`), with `selectedKind` (132 §1) so an id
                    shared by a case and a cc resolves to the right list. */}
                <div className="field">
                  <label>{t("field.case", { fallback: "Case" })}</label>
                  <MatterPicker
                    t={t}
                    cases={cases}
                    collectiveCases={collectiveCases}
                    selectedId={_caIdF ?? _ccIdF}
                    selectedKind={formMatterKind(_caIdF, _ccIdF)}
                    query={agCaseQ}
                    setQuery={sAgCaseQ}
                    placeholder={t("modal.te.search_case_short_ph", {
                      fallback: "Search case…",
                    })}
                    getRecentCases={getRecentCases}
                    currentUser={currentUser}
                    sortByTitle
                    clearLabel={t("modal.ag.general_no_case", {
                      fallback: "— General (no case) —",
                    })}
                    onClear={() =>
                      setForm((p: any) => ({ ...p, caId: "", ccId: "" }))
                    }
                    onPick={(m) => {
                      const v = String(m.id);
                      setForm((p: any) => {
                        /* BRIEF 118 §4's seed-on-pick (the matter names the
                           event through 117 §8's helper, filling rather than
                           overwriting; 131 §11 and 184 A.1 taught it the
                           collective case) STOOD HERE AND IS WITHDRAWN — 191
                           D.0, carried: the title is never prefilled from the
                           matter. The matter travels at the wire instead
                           (`m365.typedSubject`, server/src/lib/m365.js:
                           `CODE: <first word> · <title>` for Outlook; the
                           import strips it back). A pick writes the matter
                           ids and the auto-location, and leaves the title
                           exactly as the user has it. */
                        const al = genAgLocation(p.type, v);
                        return {
                          ...p,
                          caId: m._type === "ca" ? v : "",
                          ccId: m._type === "cc" ? v : "",
                          location: p._locManual ? p.location : al,
                        };
                      });
                    }}
                  />
                </div>
                {/* 154B §2 — FieldSelect's own shape; the handler below is
                    unchanged but for its argument. */}
                <FieldSelect
                  label={_fl("Type")}
                  value={form.type || ""}
                  onChange={(v) => {
                      setForm((p: any) => {
                        /* BRIEF 118 §4 — THE TYPE NO LONGER REWRITES THE TITLE.
                           This handler is where 117 §8's prefill died: the
                           `genAgTitle` arm below replaced the WHOLE title
                           whenever it did not already start with a code, and
                           the prefill deliberately does not (117 §9: the type
                           travels as a Graph category and the stored title
                           stays clean). Picking a type — the first thing anyone
                           does in a new event — put `INTRO: test - 2026/001`
                           back, which is Laurent's screenshot exactly.
                           It also destroyed hand-written titles: an existing
                           event called "Kickoff with client" became
                           `CCL: test - 2026/001` on a type change, silently.
                           WHAT SURVIVES is the in-place code SWAP, and only
                           that: a legacy title that already carries `CODE: `
                           keeps tracking its type, because editing a prefix in
                           place destroys nothing. */
                        const t = p.title || "";
                        const nt = t.match(/^[A-Z]+: /)
                          ? t.replace(/^[A-Z]+: /, v + ": ")
                          : t;
                        const al = genAgLocation(v, p.caId);
                        /* ── BRIEF 124 §1 — THE TYPE'S DECLARED MODE FOLLOWS
                           THE PICK, AND ONLY WHILE THE ANSWER IS STILL BLANK.
                           Laurent's report is this exact keystroke: pick VIR,
                           and the toggle went on saying Physical because
                           `form.meetingMode` was undefined.

                           `_modeManual` is set by the toggle itself (below) and
                           is what makes this a DEFAULT rather than a lock: once
                           the user has answered, changing the type never
                           overrules the answer. Same shape as `_locManual` on
                           the line above, which solves the identical problem for
                           the location field — a prefill that must stop
                           prefilling the moment a human disagrees with it.

                           AN EXISTING EVENT IS NOT TOUCHED AT ALL: its mode is
                           stored, is what its Graph meeting was minted on, and
                           §2's fence governs any change to it. A silent rewrite
                           on a type pick is exactly the class 118 §4 removed
                           from this handler for the title. */
                        const dm = agDefaultMode(v);
                        const seedMode =
                          !p.id && !p._modeManual && dm ? { meetingMode: dm } : {};
                        if (p.id)
                          return {
                            ...p,
                            type: v,
                            title: nt,
                            location: p._locManual ? p.location : al,
                          };
                        const on = p.templatesOn !== false;
                        const tpl = on
                          ? _buildTplItems(evtEntries, v, p.date)
                          : [];
                        return {
                          ...p,
                          type: v,
                          title: nt,
                          location: p._locManual ? p.location : al,
                          ...seedMode,
                          subtasks: _reconcileTpl(p.subtasks, tpl, on),
                        };
                      });
                  }}
                  options={[
                    ["", t("common.select_dashed", { fallback: "— Select —" })] as const,
                    /* 110 §3a's divider is 108 §6b's row now; the map's
                       parameter is renamed off `t`, the translator. */
                    ...evtTypes.map(
                      (ty: any) =>
                        [
                          String(ty.code),
                          isDividerCode(ty.code)
                            ? String(ty.desc || "")
                            : `${ty.code} — ${ty.desc}`,
                        ] as const,
                    ),
                  ]}
                />
                {/* BRIEF 87 §8 — full width, immediately after Type, exactly
                    as §8 places it. One component, shared with TaskModal. */}
                <StageLinkField
                  form={form}
                  setForm={setForm}
                  cases={cases}
                  collectiveCases={collectiveCases}
                  typeEntries={evtEntries as readonly TaxEntry[]}
                  stageTypeEntries={caseStageEntries as readonly TaxEntry[]}
                  jurisdictions={jurisdictions}
                  kind="event"
                  t={t}
                />
                <div className="field">
                  <label>{_fl("Title *")}</label>
                  <input
                    {...NO_AUTOFILL}
                    className={noAutofillClass("field-input")}
                    value={form.title || ""}
                    onChange={(e) => updateField("title", e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>
                      {t("field.date_required", { fallback: "Date *" })}
                    </label>
                    <input
                      className="field-input"
                      type="date"
                      value={form.date || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm((p: any) => {
                          if (p.id) return { ...p, date: v };
                          const on = p.templatesOn !== false;
                          const tpl = on
                            ? _buildTplItems(evtEntries, p.type, v)
                            : [];
                          return {
                            ...p,
                            date: v,
                            subtasks: _reconcileTpl(p.subtasks, tpl, on),
                          };
                        });
                      }}
                    />
                  </div>
                  <div className="field">
                    <label>{t("field.time", { fallback: "Time" })}</label>
                    {/* BRIEF 117 §5 — the shared clock. Same value contract
                        ("HH:mm" in, "HH:mm" out), five-minute rungs. */}
                    <TimeField
                      value={form.time || ""}
                      onChange={(v) => updateField("time", v)}
                    />
                  </div>
                  {/* Corr-2 (item 8) — meetings carry an end time (slots /
                      Graph span); other event types stay date+time only.
                      123 §3 — "a meeting" is the TYPE's flag now, and both
                      modes carry an end time: a physical meeting has a span
                      exactly as an online one does. */}
                  {agIsMeeting && (
                    <div className="field">
                      <label>{t("modal.field.end", { fallback: "End" })}</label>
                      <TimeField
                        value={form.endTime || ""}
                        placeholder={form.time ? hmPlus60(form.time) : undefined}
                        onChange={(v) => updateField("endTime", v)}
                      />
                    </div>
                  )}
                </div>
                <div className="field-row">
                  <ReminderLevelFields
                    S={S}
                    form={form}
                    updateField={updateField}
                    reminderOptsList={reminderOptsList}
                    itemLevelEntries={itemLevelEntries}
                    t={t}
                  />
                  {/* BRIEF 136 §3 [RULED] — HOW FIRM THIS EVENT IS, and it is
                      here because §3 as written had nowhere to set it.
                      That is §4 of this same brief, one entity over: a value
                      the data model carries, the renderer draws, and no
                      control can choose — "no means to choose anywhere either
                      when creating/posting a message, so maybe that is the
                      reason why icon does not render." Shipping the column,
                      the taxonomy and the glyph with no picker would have
                      created a second one, and the § could not be glanced
                      without a hand-written row, which SESSION_RULES calls
                      scaffolding and forbids.

                      IT SITS BESIDE REMINDER AND LEVEL because those are the
                      event's other two small enumerations and this is the row
                      they already share.

                      `archived` IS NOT OFFERED (`pickableEventStatusCodes`):
                      the archive icon owns that state, it is always visible on
                      an event (§3(4)), and a status field that could also set
                      it would be a second way to reach one state — which is
                      the whole reason it is not a value of the column. */}
                  {/* `FieldSelect` DIRECTLY, not the `S` row builder, for one
                      reason: `S` reads `form[k]`, and a new event — or one
                      created before this brief — carries no `status` at all.
                      Resolving the DISPLAYED value here shows "Confirmed"
                      (which is what NULL means) while leaving `form.status`
                      untouched, so saving an unrelated edit on an old event
                      does not write a status onto it. §3's proof survives an
                      edit, not just a deploy. */}
                  <FieldSelect
                    label={_fl("Status")}
                    value={form.status || EVENT_STATUS_DEFAULT}
                    onChange={(v: string) => updateField("status", v)}
                    options={pickableEventStatusCodes(eventStatusEntries).map(
                      (code) =>
                        [
                          code,
                          getEventStatusMeta(code, eventStatusEntries).label,
                        ] as [string, string],
                    )}
                    entries={eventStatusEntries}
                    t={t}
                  />
                </div>
                {/* ── BRIEF 122 §2 — THREE PEOPLE SURFACES BECOME ONE LIST ────
                    Laurent, glancing 2a-i: "for legibility, the New Event modal
                    should show ONE list named 'Who should attend?' listing all
                    possible candidates with checkboxes, with subdivisions: Case
                    Team members (pre checked, modifiable) + the categories in
                    Parties' pane."

                    WHAT STOOD HERE, and what each contributed to the merge:

                      MultiSelect "Should attend"  the firm's whole choosable
                        roster and the write to `assignedCollaborators`. The
                        FIELD survives — it is what the ticked team/firm rows
                        produce — and only the control is gone. 87 §10(i)(a)'s
                        finding (an event's assignment means who should ATTEND,
                        a task's means who is RESPONSIBLE) is not reopened: this
                        list is that same question asked once instead of twice.

                      EventPeopleOverview          the response status and the
                        "as of" caption. Both ride the merged list now — the
                        status on the ROW, the caption under it, because a
                        caption about when the calendar was last read is not a
                        fact about any one person.

                      AttendeesPickerSection       the checkboxes and the
                        sectioning, which is what the merged control IS. The
                        component stays for the doodle dialog (App.tsx:16396).

                    ONE LIST MEANS ONE LIST, INCLUDING THE INTERNAL MEETING.
                    `_internal` used to fork this surface into a flat two-column
                    grid; it now renders the same sectioned list, whose rows for
                    a matter-less event are the firm's own people under §5's
                    (b). `_internal` keeps its other job — telling the roster
                    effect that no matter can produce this list. */}
                {/* 122 §3a — THE SAME CONTROL, WITH ITS OTHER NAME. Same
                    population, same sections, same pre-checks; the heading and
                    the CONSEQUENCE are what differ. On a new event the list
                    says who to invite. On an existing one it says who is on the
                    event, so an untick is a removal — which is why the server's
                    union of `assigned` is withdrawn in the same commit
                    (routes/agenda.js) rather than left to argue with the word
                    "Remove". */}
                {/* ── BRIEF 123 §3 — THE CHOICE THE OLD DIALOG OPENED WITH ────
                    [MEASURED] the retired Schedule Meeting dialog carried an
                    In Person / Virtual pair at the TOP (App.tsx:16127-16179
                    @84589e7f, before 2a-i removed it). 120 §3 mapped it onto the
                    event TYPE; 123 rules that was the wrong switch — the type
                    says what kind of event, the mode says how it is held.

                    HERE, AND NOT BESIDE THE PLATFORM PICKER, because it is a
                    question about the MEETING and not about the machinery: it
                    decides whether the room picker or the platform leg renders
                    below, so it has to be answered before either is offered. The
                    participants section is where the meeting's own facts start.

                    SHOWN WHEN THE TYPE IS ONE OF THE FIRM'S MEETING TYPES (§1's
                    `meta.isMeeting`) — any number of types may be, so a firm
                    whose hearings are held by videoconference gets this pair on
                    a hearing. DEFAULT PHYSICAL on a new event: `agMode` falls
                    back through `meetingModeOf`, and the save writes the answer
                    down rather than leaving it to be re-inferred.

                    BOTH SEND INVITATIONS (RULED, Laurent 2026-08-23). The mode
                    decides the join link and the room, never whether anybody is
                    invited — which is why this sits above the attendee list
                    rather than replacing part of it. */}
                {agIsMeeting && (
                  <div className="field">
                    <label>{t("modal.ag.mode")}</label>
                    <div style={{ display: "flex", gap: 6 }}>
                      {MEETING_MODES.map((m) => (
                        <button
                          key={m}
                          type="button"
                          className="btn"
                          aria-pressed={agMode === m}
                          data-mode={m}
                          /* 124 §1 — `_modeManual` is what turns the type's
                             declared default off for the rest of this form.
                             Stamped by the ANSWER and not by the render, so a
                             default that happens to equal what the user would
                             have picked is still a default until he picks it.
                             Request-only, like `_locManual`: `svAgData`
                             destructures the `_`-prefixed fields off before the
                             payload is built, so it never reaches the wire. */
                          onClick={() =>
                            setForm((p: any) => ({
                              ...p,
                              meetingMode: m,
                              _modeManual: true,
                            }))
                          }
                          style={{
                            flex: 1,
                            padding: "6px 10px",
                            fontSize: FS.s11,
                            borderRadius: RAD.r6,
                            cursor: "pointer",
                            border: `1px solid ${agMode === m ? "var(--ac)" : "var(--br)"}`,
                            background: agMode === m ? "var(--ac)" : "transparent",
                            color: agMode === m ? "var(--bf)" : "var(--tp)",
                            fontWeight: agMode === m ? 600 : 400,
                          }}
                        >
                          {t(m === "person" ? "modal.ag.mode.person" : "modal.ag.mode.online")}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <EventAttendeesSection
                  title={t(
                    form.id
                      ? "modal.event.remove_or_invite"
                      : "modal.event.who_should_attend",
                  )}
                  rows={_picker.attendees}
                  onToggle={_toggleAttendee}
                  at={form.outlookAttendeesAt}
                />
                {/* ── BRIEF 123 §7's LINE IS GONE, AND ITS OWN RULE IS WHY ────
                    It read: "Invitations are live for this event — use
                    '{button}' to change who is invited", shown on a row with a
                    minted meeting, and §7's stated principle was THE BUTTON IS
                    NAMED FROM THE BUTTON, "so the sentence cannot come to quote
                    a label that no longer exists."

                    137 §4(d) is that day. There is no second button, and §4(b)
                    /(e) mean no control in this app changes who Outlook has
                    invited — so the sentence has nothing left to point at and
                    would be an instruction to press something that is not
                    there. Removed rather than reworded: what it warned about
                    (the app's ticks and Outlook's list drifting apart) is now
                    true of legacy rows ALONE — events minted before this brief,
                    whose Graph attendee list LexCRM can no longer touch — and
                    that is a finding for the register, not a line on a form.
                    `modal.ag.invites_live` falls quiet; its rows stay (D28). */}
                <div className="field">
                  <label>{t("field.location", { fallback: "Location" })}</label>
                  <input
                    {...NO_AUTOFILL}
                    className={noAutofillClass("field-input")}
                    value={form.location || ""}
                    onChange={(e) =>
                      setForm((p: any) => ({
                        ...p,
                        location: e.target.value,
                        _locManual: true,
                      }))
                    }
                    placeholder={t("modal.ag.location_ph", {
                      fallback: "Courtroom, address, online…",
                    })}
                  />
                  {/* ── BRIEF 124 §4 — WHICH FIELD GOES WHERE, SAID ON SCREEN.
                      Laurent ruled the event shows the ROOM and the invitation
                      carries BOTH. That is two destinations for two fields, and
                      a user who cannot see the rule will keep re-typing an
                      address into a box whose text he thinks has been ignored.

                      SHOWN ONLY WHEN A ROOM IS BOUND AND THE TYPED TEXT DIFFERS
                      — the only state in which the two destinations disagree.
                      With no room, this field IS the location everywhere and
                      there is nothing to explain.

                      `_boundRoom` is the server's derived `roomName` (agenda.js
                      `shape`, from the event's own PlaceReservation), falling
                      back to the picker's current choice so the line appears
                      the moment a room is picked rather than after a save. */}
                  {_boundRoom && form.location && form.location !== _boundRoom && (
                    <div
                      style={{
                        fontSize: FS.s10,
                        color: "var(--ts)",
                        marginTop: 4,
                        lineHeight: 1.45,
                      }}
                    >
                      {t("modal.ag.room_is_shown", { room: _boundRoom })}
                    </div>
                  )}
                </div>
                {/* 123 §3 — MODE-driven meeting machinery. This was TYPE-driven
                    (the declarative EV_MEETING_KIND map, REU/VIR); the leg now
                    follows the toggle above. PHYSICAL: room picker + pick-time
                    availability on the SHARED probe (free-text Location above
                    stays). ONLINE: platform leg — Teams mints the join URL at
                    create; Zoom/Meet take a pasted link. */}
                {agMode === "person" &&
                  (mtgRooms === null || mtgRooms.length > 0) && (
                    <RoomSelectField form={form} setForm={setForm} t={t} mtgRooms={mtgRooms} />
                  )}
                {agMode === "person" &&
                  !!form.roomPlaceId &&
                  mtgRoomConflicts && (
                    <div
                      style={{
                        fontSize: FS.s10_5,
                        padding: "7px 9px",
                        borderRadius: RAD.r6,
                        border: "1px solid var(--rd)",
                        background: "rgba(248,113,113,.08)",
                        color: "var(--rd)",
                        marginBottom: 8,
                      }}
                    >
                      {mtgRoomConflicts[0]?.badWindow
                        ? t("modal.meeting.room_window", {
                            fallback:
                              "Rooms are reservable 07:00–20:00 — adjust the meeting time.",
                          })
                        : t("modal.meeting.room_conflict", {
                            count: mtgRoomConflicts.length,
                            who: mtgRoomConflicts
                              .map((c2: any) => {
                                const h = collaborators.find((x: any) => x.id === c2.holderId);
                                return h ? coIni(h) : "?";
                              })
                              .join(", "),
                            fallback: `This room is already reserved on that slot (${mtgRoomConflicts
                              .map((c2: any) => {
                                const h = collaborators.find((x: any) => x.id === c2.holderId);
                                return h ? coIni(h) : "?";
                              })
                              .join(", ")}).`,
                          })}
                      {!mtgRoomConflicts[0]?.badWindow && canBumpRooms && (
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginTop: 5,
                            color: "var(--am)",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!form.roomOverride}
                            onChange={(e) =>
                              setForm((p: any) => ({
                                ...p,
                                roomOverride: e.target.checked,
                              }))
                            }
                          />
                          {t("modal.meeting.room_bump", {
                            fallback:
                              "Bump the existing reservation (the holder is notified)",
                          })}
                        </label>
                      )}
                    </div>
                  )}
                {agMode === "online" && (
                  <div className="field-row">
                    <div className="field">
                      <label>{t("modal.field.platform")}</label>
                      {/* 154B §2 — MenuSelect and not FieldSelect: this control
                          carries BOTH `disabled` and `title`, and 151G's ruling
                          pairs them deliberately (a disabled trigger hides its
                          hover text under WebKit). The pair is the site's
                          existing behaviour and travels verbatim; FieldSelect
                          has no `title` to carry it through. */}
                      <MenuSelect
                        className="field-input"
                        value={
                          form.platform ||
                          (mtgPlatforms.teams
                            ? "teams"
                            : mtgPlatforms.zoom
                              ? "zoom"
                              : "gmeet")
                        }
                        disabled={!!form.id}
                        title={
                          form.id
                            ? t("modal.ag.platform_locked", {
                                fallback: "The platform is set at creation",
                              })
                            : undefined
                        }
                        onChange={(v) => updateField("platform", v)}
                        options={[
                          ...(mtgPlatforms.teams || form.platform === "teams"
                            ? [
                                [
                                  "teams",
                                  t("platform.teams", {
                                    fallback: "Microsoft Teams",
                                  }),
                                ] as const,
                              ]
                            : []),
                          ...(mtgPlatforms.zoom || form.platform === "zoom"
                            ? [
                                [
                                  "zoom",
                                  t("platform.zoom", { fallback: "Zoom" }),
                                ] as const,
                              ]
                            : []),
                          ...(mtgPlatforms.gmeet || form.platform === "gmeet"
                            ? [
                                [
                                  "gmeet",
                                  t("platform.gmeet", { fallback: "Google Meet" }),
                                ] as const,
                              ]
                            : []),
                        ]}
                      />
                    </div>
                    {/* BRIEF 119 §1d — 117 §6b's CHIP LIST IS RETIRED, now that
                        its replacement exists. The verdict kept it only because
                        removing it earlier would have left no invitee choice at
                        all (`briefs/118_verdict-s6b.md`, option 3): it invited
                        COLLABORATORS ONLY, which is exactly Laurent's report.
                        The Schedule Meeting dialog "Save & invite" opens (§1c)
                        offers the same colleagues AND the matter's clients,
                        opposing counsel and experts, in §5's three named groups
                        — so nothing is lost and the gap is closed.

                        ITS i18n ROWS STAY SEEDED AND UNREAD (D28): a key with no
                        call site costs nothing, and deleting rows is how a
                        later caller silently ships English. `modal.ag.invitees`
                        keeps its three rows.

                        THE REQUEST FIELD STAYS TOO, and that is deliberate
                        rather than an oversight: `routes/agenda.js` still reads
                        `inviteeIds` off the body (:487) and `saveAgendaEvent`
                        still strips it, so a client that sends one is still
                        honoured. What is gone is the only SCREEN that produced
                        it — an untouched picker always sent nothing and the
                        route fell back to the assignees, which is precisely
                        what it now does for every save on this path. */}
                    {(form.platform ||
                      (mtgPlatforms.teams
                        ? "teams"
                        : mtgPlatforms.zoom
                          ? "zoom"
                          : "gmeet")) !== "teams" && (
                      <div className="field">
                        <label>
                          {t("modal.meeting.link", {
                            fallback: "Meeting link (optional)",
                          })}
                        </label>
                        <input
                          {...NO_AUTOFILL}
                          className={noAutofillClass("field-input")}
                          type="url"
                          value={form.meetingUrl || ""}
                          placeholder="https://…"
                          onChange={(e) => updateField("meetingUrl", e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                )}
                {/* 120 §3 put the invitee checkboxes here, under the meeting
                    machinery. 122 §2 merges them into the one list ABOVE, with
                    "Should attend" and the response overview — the people
                    question is asked once, where the event's own fields are,
                    and not a second time under the platform picker. */}

                <SubtaskChecklist {...({} as any)}
                  locked={false}
                  /* BRIEF 87 §6 E3 — the Template-subtasks button, which
                     used to float in a field of its own ABOVE the section
                     it acts on. It renders under the SUBTASKS heading now,
                     inside the section, which is what it has always been
                     part of. */
                  headerSlot={
                    <TemplateSubtasksButton
                      kind="event"
                      form={form}
                      setForm={setForm}
                      entries={evtEntries}
                      rows={agendaEvents}
                      setRows={setAgendaEvents}
                      t={t}
                      toast={toast}
                    />
                  }
                  parentEventId={form.id || null}
                  initialSubtasks={form.id ? undefined : form.subtasks || []}
                  controlledItems={form.id ? undefined : form.subtasks || []}
                  canWrite={true}
                  /* Brief 86 §2e-1 — the assignment column, showing initials.
                     Nothing was built for it: SubtaskChecklist has carried the
                     picker since C59 behind `assignable`, and Subtask.
                     assignedCoId is parent-agnostic — subtasks.js:143-144
                     already branches parentTaskId vs parentEventId when it
                     records the delegation. The Event modal simply never
                     passed the four props the Task modal passes, so an event's
                     subtask could be assigned by the API and by no screen.
                     The gate is the AGENDA edit key, not the tasks key: same
                     question, this modal's own subject. */
                  co={collaborators}
                  currentUser={currentUser}
                  assignable
                  canAssignAll={
                    !!_appIsAdmin ||
                    _appPerms.can("caseView.agenda.edit", _matterScope) // 185 E.1 — the form's own matter, not a `caseId` it never carries
                  }
                  onLocalChange={(arr: any) => updateField("subtasks", arr)}
                  onItemsChange={(arr: any) => {
                    if (form.id)
                      setAgendaEvents((prev: any) =>
                        prev.map((x: any) =>
                          x.id === form.id ? { ...x, subtasks: arr } : x,
                        ),
                      );
                  }}
                  isAdmin={true}
                />
                {/* BRIEF 87 §4 + §6 E5 — THE DOCUMENT LIST, replacing the
                    single docLink field. §0(3): the EVENT modal gains the TASK
                    modal's Documents section and never the reverse. The pane is
                    literally the Task one, parameterised by `eventId` — one
                    implementation, because "the value is that the two match".

                    AgendaEvent.docLink IS KEPT AND STILL RENDERS. The migration
                    deliberately did not backfill it (three surfaces still read
                    the scalar: CaseEventRow.tsx:394, Dashboard.tsx:1228, and
                    this modal), so an event created before the list keeps its
                    one link and shows it here as a read-only legacy line rather
                    than losing it. Editing moved to the list; the old value is
                    visible, not editable, and not duplicated into the list —
                    which would have been the same link twice on one screen. */}
                <TaskDocsPane
                  eventId={form.id || null}
                  locked={false}
                  initialDocs={
                    form.id
                      ? (() => {
                          const _ev = (agendaEvents || []).find(
                            (x: { id?: number }) => x.id === form.id,
                          );
                          return _ev && Array.isArray(_ev.docs) ? _ev.docs : undefined;
                        })()
                      : form.docs || []
                  }
                  canWrite={true}
                  onLocalChange={(arr: unknown[]) => updateField("docs", arr)}
                  /* 110 §1a — the matter binding, derived exactly as the
                     retired OneDriveDocButton derived it (collapse the form's
                     four matter aliases — this modal's live one is `caId` —
                     disambiguate by list membership); the pane owns the button
                     and the write now. A matterless or unresolvable matter
                     keeps the old unconfined browse ({}). */
                  oneDrive={(() => {
                    const _cid =
                      +(form.caseId || form._ci || form.caId || form.ccId || 0) ||
                      null;
                    if (_cid && cases.some((c: any) => c.id === _cid))
                      return { caseId: _cid };
                    if (_cid && collectiveCases.some((c: any) => c.id === _cid))
                      return { ccId: _cid };
                    return {};
                  })()}
                  /* 151C §6 — the purple itemLink, TaskModal's twin note in
                     full: the stageId pen is retired from this flow
                     (StageLinkField above keeps the belongs-to fact), EDIT
                     MODE ONLY (an unsaved event has no id to link). `_matter`
                     already resolves this modal's caId-carries-both shape;
                     its kind is the form's own ccId presence. */
                  stageLink={
                    Number(form.id || 0) > 0 &&
                    _matter &&
                    Array.isArray(_matter.stages) &&
                    _matter.stages.length > 0
                      ? {
                          stages: _matter.stages,
                          matterKind: form.ccId ? ("cc" as const) : ("ca" as const),
                          matterId: _matter.id,
                          matterRow: _matter,
                        }
                      : undefined
                  }
                />
                {form.docLink ? (
                  <div className="field" style={{ marginTop: 4 }}>
                    <label style={{ fontSize: FS.s9_5, color: "var(--tm)" }}>
                      {t("event.doc_link_legacy", {
                        fallback: "Document link (before the list — read only)",
                      })}
                    </label>
                    <a
                      href={form.docLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: FS.s10_5, color: "var(--ac)", wordBreak: "break-all" }}
                    >
                      {form.docLink}
                    </a>
                  </div>
                ) : null}
                {/* BRIEF 126 §9 — 125's `OpenEmailButton` STOOD HERE AND IS
                    RETIRED, for the reason its twin in TaskModal states: the
                    documents pane just above is the affordance now, and the
                    drop writes its row unconditionally. `AgendaEvent.emailGraphId`
                    stays as provenance; it is no longer a control. */}
                {T("Notes", "notes")}
              </>
          </ModalLayout>
  );
}
